import { z } from 'zod';

import type {
  OrderFulfillment,
  OrderLine,
  PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody,
  PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody,
} from '@nucleus-commerce/api-client/admin';

import { openQuantity } from './order-rules';

const whole = z
  .string()
  .trim()
  .refine((v) => /^\d+$/.test(v), { error: 'orders:fulfil.validation.wholeNumber' });

const trackingFields = {
  carrier: z.string().trim().max(80),
  trackingNumber: z.string().trim().max(120),
  trackingUrl: z
    .string()
    .trim()
    .refine((v) => v === '' || URL.canParse(v), {
      error: 'orders:fulfil.validation.trackingUrl',
    }),
  notifyCustomer: z.boolean(),
  note: z.string().trim().max(2000),
};

/**
 * One row per order line with something left to ship. Quantities are typed as text so an empty
 * box is a clear zero; the backend's own cap (what is still open) is checked here too so a stale
 * page gets a field error rather than a 422. `locationId` is blank when the store has one
 * location or staff leave the default.
 */
export function fulfillmentSchema(lines: readonly OrderLine[]) {
  const open = new Map(lines.map((line) => [line.id, openQuantity(line)]));
  return z
    .object({
      lines: z.array(z.object({ orderLineId: z.string(), quantity: whole })),
      shipNow: z.boolean(),
      locationId: z.string(),
      ...trackingFields,
    })
    .superRefine((v, ctx) => {
      let total = 0;
      v.lines.forEach((line, index) => {
        const quantity = Number(line.quantity);
        total += quantity;
        const max = open.get(line.orderLineId) ?? 0;
        if (quantity > max) {
          ctx.addIssue({
            code: 'custom',
            path: ['lines', index, 'quantity'],
            message: 'orders:fulfil.validation.overOpen',
          });
        }
      });
      if (total === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['lines'],
          message: 'orders:fulfil.validation.nothingSelected',
        });
      }
    });
}

export type FulfillmentFormInput = z.input<ReturnType<typeof fulfillmentSchema>>;
export type FulfillmentFormOutput = z.output<ReturnType<typeof fulfillmentSchema>>;

/** Every open line preselected in full: the common case is "ship the rest". */
export function emptyFulfillmentForm(lines: readonly OrderLine[]): FulfillmentFormInput {
  return {
    lines: lines
      .filter((line) => openQuantity(line) > 0)
      .map((line) => ({ orderLineId: line.id, quantity: String(openQuantity(line)) })),
    shipNow: true,
    locationId: '',
    carrier: '',
    trackingNumber: '',
    trackingUrl: '',
    notifyCustomer: true,
    note: '',
  };
}

/** Zero-quantity rows are dropped; blank optionals become null so the backend clears them. */
export function toFulfillmentBody(
  values: FulfillmentFormOutput,
): PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody {
  return {
    lines: values.lines
      .map((line) => ({ orderLineId: line.orderLineId, quantity: Number(line.quantity) }))
      .filter((line) => line.quantity > 0),
    status: values.shipNow ? 'shipped' : 'pending',
    ...(values.locationId ? { locationId: values.locationId } : {}),
    carrier: values.carrier || null,
    trackingNumber: values.trackingNumber || null,
    trackingUrl: values.trackingUrl || null,
    notifyCustomer: values.notifyCustomer,
    note: values.note || null,
  };
}

/** Tracking and note of a recorded parcel; status moves through its own buttons. */
export const fulfillmentUpdateSchema = z.object(trackingFields);

export type FulfillmentUpdateInput = z.input<typeof fulfillmentUpdateSchema>;
export type FulfillmentUpdateOutput = z.output<typeof fulfillmentUpdateSchema>;

/**
 * `notifyCustomer` is not stored on the parcel: the backend emails the tracking change when it
 * is on, so the form starts with it on only for a parcel that already shipped.
 */
export function fromFulfillment(fulfillment: OrderFulfillment): FulfillmentUpdateInput {
  return {
    carrier: fulfillment.carrier ?? '',
    trackingNumber: fulfillment.trackingNumber ?? '',
    trackingUrl: fulfillment.trackingUrl ?? '',
    notifyCustomer: fulfillment.status !== 'pending',
    note: fulfillment.note ?? '',
  };
}

/** Only fields that differ from the parcel go out, blanks as null; an unchanged form is empty. */
export function toFulfillmentUpdate(
  values: FulfillmentUpdateOutput,
  fulfillment: OrderFulfillment,
): PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody {
  const body: PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody = {};
  const next = {
    carrier: values.carrier || null,
    trackingNumber: values.trackingNumber || null,
    trackingUrl: values.trackingUrl || null,
    note: values.note || null,
  };
  for (const key of ['carrier', 'trackingNumber', 'trackingUrl', 'note'] as const) {
    if (next[key] !== fulfillment[key]) body[key] = next[key];
  }
  if (Object.keys(body).length > 0) body.notifyCustomer = values.notifyCustomer;
  return body;
}
